import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Film } from "@/types";
import { films } from "@/mocks/films";

const hotKeywords = ["科幻", "悬疑", "古装", "治愈", "犯罪"];

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return films
      .filter((film) => {
        const haystack = [
          film.title,
          film.category,
          film.region,
          ...film.genres,
          ...film.actors,
          ...film.tags,
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(kw);
      })
      .slice(0, 8);
  }, [keyword]);

  if (!open) return null;

  const goDetail = (film: Film) => {
    onClose();
    setKeyword("");
    navigate(`/detail/${film.id}`);
  };

  return (
    <div className="fixed inset-0 z-[70] anim-fade">
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative mx-auto mt-16 w-[92%] max-w-2xl rounded-2xl border border-background-200 bg-background-50 p-4 md:p-6">
        <div className="flex items-center gap-3 border-b border-background-200 pb-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-search-line text-lg"></i>
          </span>
          <input
            autoFocus
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影视名称、演员或类型..."
            className="w-full bg-transparent text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none md:text-lg"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100"
            aria-label="关闭搜索"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {keyword.trim() ? (
          <div className="mt-3 max-h-[60vh] overflow-y-auto">
            {results.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {results.map((film) => (
                  <li key={film.id}>
                    <button
                      type="button"
                      onClick={() => goDetail(film)}
                      className="flex w-full cursor-pointer items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={film.cover}
                          alt={film.title}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-foreground-900">
                          {film.title}
                        </span>
                        <span className="block truncate text-xs text-foreground-500">
                          {film.category} · {film.year} · {film.genres.join(" / ")}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm font-semibold text-accent-600">
                        {film.rating.toFixed(1)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </span>
                <p className="text-sm text-foreground-500">
                  未找到相关影视作品，换个关键词试试吧
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4">
            <p className="mb-2 text-xs font-medium text-foreground-500">热门搜索</p>
            <div className="flex flex-wrap gap-2">
              {hotKeywords.map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => setKeyword(kw)}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}